import {ITourService, TourEvents} from "../ITourService";
import {LTServices} from "../../lt-ng/lt-services";
import IMapService = LTServices.IMapService;
import {resetMap, typeIntoInput} from "../../tours/oe/commands";
import {
    _workAroundMoveNext,
    delay,
    onActivate,
    onReset,
    simulateOpenLayersEvent, timeoutHandler,
    TourStatus,
    waitFor
} from "../../tours/commands";
import Driver from "driver.js";
import * as OETourSteps from "../../tours/oe/steps";
import {GeoAppEvents} from "../../controllers/events/geoapp_events";
import {debounce} from "../../utility/debounce";
import {LoggingEvent} from "../IUsageLoggingService";
import {Panes} from "../../controllers/panes";
import {SearchService} from "../search";
import {ICoordinateAdjustmentService} from "../coordinate-adjustment";
import {L} from "../../lt-ng/landcare-toolkit";
import {FeaturesEvents} from "../events/FeaturesEvents";
import {linzDemo, linzDemoAction} from "../../tours/oe/steps";
import {IReportPresenter} from "./report-presenter/IReportPresenter";
import {linzParcelArea, linzParcelMostIntensiveSustainableLandUse} from "./OETourServiceData";
import {isPortrait} from "./util";
import {Extent} from "ol/extent";
import {Coordinate} from "ol/coordinate";
// ignore ts error, rollup takes care of importing the json
// @ts-ignore
import exampleReportJSON from './report-example.json';
import {StateService} from "@uirouter/angularjs";
// import {StateNames} from "../../router/oe-layers-router";
import {ILayerTreeManipulationService} from "./layer-tree-manipulation";

export type TourFn = (driver?: Driver, startStepId?: string, restarted?: boolean) => TourStatus

const taranakiExtent: Extent = [ 1682040.1976439261, 5640153.151768415, 1701420.5367287656, 5659519.176860183 ];
const taranakiCentre: Coordinate = [ 1691732.34180002, 5649811.76060001];

export class OETourService implements ITourService {
    public static $inject = [
        'landcareMapService',
        '$rootScope',
        'searchService',
        'coordinateAdjustmentService',
        'reportPresenterService',
        '$state',
        '$timeout',
        'layerTreeManipulationService'
    ];
    private closeListener: (ev: MouseEvent) => void;
    private keyUpListener: (ev: KeyboardEvent) => void;

    constructor(protected mapService: IMapService,
                protected $rootScope: ng.IRootScopeService,
                protected searchService: SearchService,
                protected coordinateAdjustmentService: ICoordinateAdjustmentService,
                protected reportPresentationService: IReportPresenter,
                protected $state: StateService,
                protected $timeout: ng.ITimeoutService,
                protected layerTreeManipulationService: ILayerTreeManipulationService) {

        $rootScope.$on(TourEvents.start, () => this.startTour());
    }

    protected _tourLoading = false;

    protected desktopTourFn = (driver?: Driver, stepId?: string, restarted?: boolean): TourStatus => {
        return this.startDesktopTour(driver, stepId, restarted);
    };

    protected tabletTourFn = (driver?: Driver, stepId?: string, restarted?: boolean): TourStatus => {
        return this.startTabletTour(driver, stepId, restarted);
    };

    protected mobileTourFn = (driver?: Driver, stepId?: string, restarted?: boolean): TourStatus => {
        return this.startMobileTour(driver, stepId, restarted);
    };

    onReset(): ng.IPromise<void> {
        /*resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);*/

        this.$rootScope.$broadcast(GeoAppEvents.resetMapAndLayers);

        return this.$timeout(() => {
            this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);
            this.$rootScope.$broadcast(FeaturesEvents.hideReport);
            this.$rootScope.$broadcast(Panes.Events.RESET_PANES);
        }, 1);
    }

    getCurrentTourFn(): TourFn {
        const width = window.innerWidth;

        const sm = 768;
        const md = 1050;

        if (window.matchMedia) {

            if (window.matchMedia(`(max-width: ${sm - 1}px)`).matches) {
                return this.mobileTourFn;
            } else if (window.matchMedia(`(max-width: ${md - 1}px)`).matches) {
                return this.tabletTourFn;
            }
            return this.desktopTourFn;

        } else if (width >= md) {
            return this.desktopTourFn;
        } else if (width >= sm) {
            return this.tabletTourFn;
        } else{
            return this.mobileTourFn;
        }
    }

    isTourLoading(): boolean {
        return this._tourLoading;
    }

    startTour(): void {
        this._tourLoading = true;
        this.mapService.mapPromise.then(map => {
            this._tourLoading = false;
            resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);
            this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);
            return waitFor('.pane-container:not(.pane-collapse-layer-selector) .pane-meta-layer-selector')
                .catch(timeoutHandler(undefined));
        })
        .then(() => {
            let currentTourFn = this.getCurrentTourFn();
            let currentTourStatus = currentTourFn(undefined, undefined, undefined);

            waitFor("#driver-popover-item")
                .then(() => {
                    const elem = document.getElementById('driver-popover-item') as HTMLElement;
                    const rect = elem.getBoundingClientRect();
                    if (rect.left < 0
                        || rect.top < 0
                        || rect.right > window.innerWidth
                        || rect.bottom > window.innerHeight
                    ) {
                        currentTourStatus().driver.refresh();
                    }
                })
                .catch(() => {
                    console.info("waitFor #driver-popover-item timed out");
                });

            // because we have different tours depending on screen size,
            // we want to change tours on the window resize event

            let handlingChange = false;

            const tourFnIfChanged = () => {
                const nextTourFn = this.getCurrentTourFn();
                return nextTourFn !== currentTourFn ? nextTourFn : null;
            };

            const onResize = debounce(() => {
                console.log('resize function');
                if (!handlingChange) {
                    handlingChange = true;
                    const driver = currentTourStatus().driver;
                    if (!driver['isActivated']) {
                        window.removeEventListener('resize', onResize);
                        handlingChange = false;
                        return;
                    }

                    const nextTourFn = tourFnIfChanged();
                    if (nextTourFn) {
                        // const currentStepIdx = <number> driver['currentStep'];
                        // const currentStep = driver['steps'][currentStepIdx];
                        // const stepId: string = currentStep && currentStep['options'] && currentStep['options']['_id'];;
                        console.log('changing active tour due to resize');
                        currentTourStatus = nextTourFn(driver, null, true);
                        currentTourFn = nextTourFn
                    }
                }
                handlingChange = false;
            }, 100);

            window.addEventListener('resize', onResize);

            console.log(window.screen && window.screen['orientation'], window.orientation);

            const onOrientation = () => {
                console.log('orientationchange handler');
                console.log(window.screen && window.screen['orientation'], window.orientation);

                const driver = currentTourStatus().driver;

                if (!driver || !driver['isActivated']) {
                    if (window.screen && window.screen.orientation) {
                        window.screen.orientation.removeEventListener('change', onOrientation);
                    } else {
                        window.removeEventListener('orientationchange', onOrientation);
                    }

                    handlingChange = false;
                    return;
                }

                if (tourFnIfChanged()) {
                    console.log('Orientation listener, changing tour size');
                    requestAnimationFrame(onResize);
                    return;
                }

                setTimeout(() => {
                    requestAnimationFrame(() => {
                        try {
                            if (!handlingChange) {
                                handlingChange = true;
                                console.log('resetting tour');
                                this.getCurrentTourFn()(driver, null, true);
                            } else {
                                console.log('not resetting tour');
                            }
                        } catch (e) {
                            console.warn(e);
                        } finally {
                            handlingChange = false;
                        }
                    });
                }, 30);
            };

            if (window.screen && window.screen.orientation && window.screen.orientation.addEventListener) {
                window.screen.orientation.addEventListener('change', onOrientation);
            } else {
                window.addEventListener("orientationchange", onOrientation);
            }


            // the following are used in logging usage to our logger and Google Analytics
            // notify others that the tour has started
            // const startTourEvent = new Event("TOUR_START");
            const startTourLoggingEvent = <LoggingEvent> new Event("LOG_EVENT");
            startTourLoggingEvent.category = "TOUR_START";

            // disable the map's keyboard controls while the tour is active,
            // to allow the arrow keys to be used to step through the tour
            // without moving the map at the same time
            this.mapService.keyboardPan.setActive(false);
            this.mapService.keyboardZoom.setActive(false);

            let tourActive = true;

            window.dispatchEvent(startTourLoggingEvent);
            this.closeListener = ev => {
                const target = ev.target;
                const classList = <DOMTokenList> target["classList"];
                if (classList.contains('driver-close-btn')) {
                    tourActive = false;
                    const loggingEvent = <LoggingEvent> new Event("LOG_EVENT");
                    loggingEvent.category = "TOUR_CLOSE";
                    window.dispatchEvent(loggingEvent);
                    window.removeEventListener("click", this.closeListener);
                    window.removeEventListener("keyup", this.keyUpListener);
                    this.mapService.keyboardPan.setActive(true);
                    this.mapService.keyboardZoom.setActive(true);
                }
            };

            this.keyUpListener = ev => {
                if (!tourActive) return;
                if (ev.key === "Escape") {
                    tourActive = false;
                    this.mapService.keyboardPan.setActive(true);
                    this.mapService.keyboardZoom.setActive(true);
                    const loggingEvent = <LoggingEvent> new Event("LOG_EVENT");
                    loggingEvent.category = "TOUR_CLOSE";
                    window.dispatchEvent(loggingEvent);
                    window.removeEventListener("click", this.closeListener);
                    window.removeEventListener("keyup", this.keyUpListener);
                }
            }

            // notify others that the tour has been closed
            window.addEventListener('click', this.closeListener);
            window.addEventListener('keyup', this.keyUpListener)
        });
    }

    showExampleAreaReport() {
        return fetch(exampleReportJSON)
            .then(response => response.json())
            .then(report => {
                this.reportPresentationService.showReport(
                    report,
                    {
                        areaAggregateQueryFn: () => {
                            return {
                                request: Promise.resolve(linzParcelMostIntensiveSustainableLandUse)
                            };
                        },
                        geoFeatureAreaFn: () => {
                            return {
                                request: Promise.resolve(linzParcelArea)
                            };
                        },
                        geoFeatureFn: () => {
                            return {
                                request: Promise.resolve(report.subject)
                            };
                        }
                    });
            })
            .then(() => waitFor('#report-container'))
            .then(container => {
                container.style.zIndex = "100004 !important";
            })
            .catch(timeoutHandler(undefined));
    }

    fitTaranaki() {
        const map = this.mapService.map;
        map.getView().fit(taranakiExtent, { size: map.getSize(), padding: this.coordinateAdjustmentService.padding([10, 10, 10, 10])});
    }

    startDesktopTour(useDriver: Driver = null,
                     startStepId: string = null,
                     restarted: boolean = false): TourStatus {
        const driverOptions = {
            animate: true,
            stageBackground: "transparent",
            allowClose: false,
            onReset,
            onNext: () => {
                console.log('global onNext')
            }
        };

        onActivate();

        let driver = useDriver || new Driver(driverOptions);

        let steps;

        const workAroundMoveNext = (id) => _workAroundMoveNext(driver, steps, id);

        const simpleNext = (aStep) => {
            driver.preventMove();
            requestAnimationFrame(() => workAroundMoveNext(aStep));
        };

        /*const linzBtnName = `Report based on LINZ Parcels`;*/
        const showPane = (paneId: string) => {
            this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, paneId);
        };

        steps = [
            {
                _id: 'a',
                element: '.pane-container:not(.pane-collapse-layer-selector) .meta-layer-selector-container',
                popover: {
                    ...OETourSteps.intro,
                    position: 'mid-center'
                },
                onNext: () => {
                    driver.preventMove();

                    resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);
                    delay(10)
                        // .then(() => waitFor("div.layer-selector-body:not(.ng-hide) .panel-group .panel:nth-child(2) .expand-button"))
                        .then(() => waitFor("div.layer-selector-body:not(.ng-hide) .panel[data-title='About Land Capability'] [data-action=expandTopic]"))
                        .then(btn => {
                            if (btn.querySelector('.icon-plus')) {
                                btn.click();
                            }

                            return waitFor('.panel-collapse.collapse.in:not(.ng-hide)');
                        })
                        .then(panel => {
                            const items = panel.querySelectorAll('.flex-grid-item input');
                            let currentAction = Promise.resolve();

                            items.forEach((item: HTMLInputElement) => {
                                currentAction = currentAction
                                    .then(() => {
                                        if (!item.checked) {
                                            (<HTMLElement> item).click();
                                            return delay(250);
                                        }
                                    });
                            });

                            return currentAction;
                        })
                        // .then(() => waitFor("div.layer-selector-body:not(.ng-hide) .panel-group .panel:nth-child(3) .panel-heading input"))
                        .then(() => waitFor("div.layer-selector-body:not(.ng-hide) .panel-group .panel[data-title='About Habitats'] .panel-heading input"))
                        .then((checkbox: HTMLInputElement) => checkbox.click())
                        .then(() => {
                            this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false);
                            this.$rootScope.$broadcast('layerSelector:updateIncludedLayers');
                            showPane(Panes.Ids.LAYERS);
                            return workAroundMoveNext('b');
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'b',
                element: '#PaneLayers',
                popover: {
                    ...OETourSteps.myLayers,
                    position: 'right-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);
                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);
                    workAroundMoveNext('a');
                },
                onNext: () => simpleNext('c')
            },
            {
                _id: 'c',
                element: ".map-pane-left #PaneLayers [data-action=showLayerSelector]",
                stageBackground: "#fff",
                popover: {
                    ...OETourSteps.changeLayers,
                    position: 'right-center'
                },
                onPrevious: () => simpleNext('b'),
                onNext: () => simpleNext('d')
            },
            {
                _id: 'd',
                element: 'main',
                popover: {
                    ...OETourSteps.mapPage,
                    position: 'mid-center'
                },
                onPrevious: () => simpleNext('c'),
                /*() => {
                    driver.preventMove();
                    waitFor(".map-pane-left #PaneLayers [data-action=showLayerSelector]")
                        .then(elem => {
                            elem.scrollIntoView();
                            return workAroundMoveNext('c');
                        })
                },*/
                onNext: () => {
                    driver.preventMove();
                    showPane(Panes.Ids.Legend);
                    delay(250)
                        .then(() => workAroundMoveNext('e'));
                }
            },
            {
                _id: 'e',
                element: '.map-pane-bottom',
                popover: {
                    ...OETourSteps.legend,
                    position: 'top-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.Legend);
                    workAroundMoveNext('d');
                },
                onNext: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.Legend);
                    workAroundMoveNext('f');
                }
            },
            {
                _id: 'f',
                element: '#PaneSearch',
                popover: {
                    ...OETourSteps.search,
                    position: 'right-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    showPane(Panes.Ids.Legend);
                    delay(250)
                        .then(() => workAroundMoveNext('e'));
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('#PaneSearch input')
                        .then((input: HTMLInputElement) => {
                            return typeIntoInput(input, "Mount Taranaki")
                        }).then(() => {
                            const coordinate = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCentre, window['map'], 8);
                            this.searchService.animateZoomTo(coordinate, 8);
                            this.searchService.highlightLocation(coordinate, 4000);
                        })
                        .then(() => delay(1500))
                        .then(() => waitFor('#PaneSearch input'))
                        .then((search: HTMLInputElement) => {
                            search.value = '';
                            return workAroundMoveNext('g')
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'g',
                element: '.ol-zoom',
                popover: {
                    ...OETourSteps.zoom,
                    position: 'right-center'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(L.Events.OL_RESET_EXTENT, {
                        detail: {
                            padding: this.coordinateAdjustmentService.padding(),
                            duration: 500
                        }
                    });
                    simpleNext('f')
                },
                onNext: () => simpleNext('h')
            },
            {
                _id: 'h',
                element: '#PaneLayers',
                popover: {
                    ...OETourSteps.mapLayers,
                    position: 'right-center'
                },
                onPrevious: () => simpleNext('g'),
                onNext: () => {
                    driver.preventMove();
                    waitFor('#PaneLayers label[title="Most intensive, sustainable land use possible"]')
                        .then((label) => {
                            label.click();
                            return delay(100);
                        })
                        .then(() => {
                            showPane(Panes.Ids.FEATURES);
                            return waitFor('.pane-container:not(.pane-collapse-right)')
                        })
                        .then(() => delay(250))
                        .then(() => workAroundMoveNext('i'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'i',
                element: '#PaneFeatures',
                popover: {
                    ...OETourSteps.reportPane,
                    position: 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.FEATURES);
                    waitFor('#PaneLayers label[title="Suitability for Arable Cropping"]')
                        .then(() => workAroundMoveNext('h'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'j',
                stageBackground: "#fff",
                element: '#PaneFeatures .add-feature-action',
                popover: {
                    ...OETourSteps.dropPinA,
                    position: 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    waitFor('[data-action="toggleDropPinMode"]')
                        .then(btn => {
                            btn.click();
                        })
                        .then(() => workAroundMoveNext('i'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => simpleNext('k')
            },
            {
                _id: 'k',
                element: 'main',
                popover: {
                    ...OETourSteps.dropPinB,
                    position: 'mid-center'
                },
                onPrevious: () => simpleNext('j'),
                onNext: () => {
                    // drop a pin
                    driver.preventMove();

                    const coord = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCentre, window['map'], 8);
                    this.$rootScope.$broadcast(GeoAppEvents.queryPoint, coord);
                    waitFor('#PaneFeatures .report-preview')
                        .then(() => workAroundMoveNext('l'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'l',
                element: '#PaneFeatures .pane-content',
                stageBackground: "#fff",
                popover: {
                    ...OETourSteps.pinSummary,
                    position: 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    waitFor('#PaneFeatures .add-feature-action')
                        .then(() => waitFor('[data-action="toggleDropPinMode"]'))
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('k'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    waitFor('#PaneFeatures .add-feature-action')
                        .then(() => workAroundMoveNext('m'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'm',
                element: '#PaneFeatures .features-container',
                stageBackground: "#fff",
                popover: {
                    ...OETourSteps.areaReports,
                    position: 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    const coord = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCentre, window['map'], 8);
                    this.$rootScope.$broadcast(GeoAppEvents.queryPoint, coord);
                    delay(250)
                        .then(() => {

                            workAroundMoveNext('l')
                        })
                },
                onNext: () => simpleNext('n')
            },
            {
                _id: 'n',
                element: '#PaneFeatures [data-action=togglePredefinedFeatureMode][title*=LINZ]',
                stageBackground: "#fff",
                popover: {
                    ...linzDemo,
                    position: 'left-center'
                },
                onPrevious: () => simpleNext('m'),
                onNext: () => {
                    driver.preventMove();
                    waitFor('#PaneFeatures [data-action=togglePredefinedFeatureMode][title*=LINZ]')
                        .then(btn => btn.click())
                        .then(() => {
                            return this.mapService.getActiveClickInteraction();
                        })
                        .then((interaction) => simulateOpenLayersEvent(this.mapService.map, interaction, 'click', 0, 0))
                        .then(() => {
                            this.fitTaranaki();
                            workAroundMoveNext('o')
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'o',
                element: '#PaneFeatures .pane-content',
                popover: {
                    ...OETourSteps.linzSummary,
                    position: 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    workAroundMoveNext('n');
                },
                onNext: () => simpleNext('p')
            },
            {
                _id: 'p',
                element: '#PaneFeatures .report-preview [data-action="createFullReport"]',
                stageBackground: '#fff',
                popover: {
                    ...OETourSteps.linzFullReport,
                    position: 'left-center'
                },
                onPrevious: () => simpleNext('o'),
                onNext: () => {
                    // display a complete report... somehow
                    driver.preventMove();
                    this.showExampleAreaReport()
                        .then(() => workAroundMoveNext('q'));
                }
            },
            {
                _id: 'q',
                element: '.fullscreen-report',
                popover: {
                    ...OETourSteps.fullReports,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    waitFor('.fullscreen-report i.close-button')
                        .then(btn => {
                            btn.click();
                            workAroundMoveNext('p');
                        })
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    // display a complete report... somehow
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.FEATURES);
                    driver.preventMove();
                    waitFor('.fullscreen-report i.close-button')
                        .then(btn => btn.click())
                        .then(() => delay(250))
                        .then(() => workAroundMoveNext('r'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'r',
                element: '[data-action="showPrintWindow"]',
                stageBackground: '#fff',
                popover: {
                    ...OETourSteps.printTool,
                    position: 'bottom-center'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.FEATURES);
                    driver.preventMove();
                    this.fitTaranaki();
                    waitFor('#PaneFeatures [data-action=togglePredefinedFeatureMode][title*=LINZ]')
                        .then(btn => btn.click())
                        .then(() => {
                            return this.mapService.getActiveClickInteraction();
                        })
                        .then((interaction) => simulateOpenLayersEvent(this.mapService.map, interaction,'click', 0, 0))
                        .then(() => this.showExampleAreaReport())
                        .then(() => workAroundMoveNext('q'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('[data-action="showPrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('s'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 's',
                element: '.print-pane',
                popover: {
                    ...OETourSteps.printWindow,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    waitFor('[data-action="closePrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('r'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();

                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);

                    waitFor('[data-action="closePrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('t'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 't',
                element: 'main',
                popover: {
                    ...OETourSteps.conclusion,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false);

                    waitFor('[data-action="showPrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('s'))
                        .catch(timeoutHandler(driver));
                }
            }
        ];

        if (restarted) {
            steps.unshift({
                _id: 'restarted',
                element: '#ng-body',
                popover: {
                    ...OETourSteps.tourRestarted,
                    position: 'mid-center'
                },
                onNext: () => {
                    driver.preventMove();
                    this.onReset();
                    steps.shift();
                    workAroundMoveNext('a');
                }
            })
        }

        driver.defineSteps(steps);

        // startWhenReady(driver, appScope, appElement, true);

        driver.start(0);

        return <TourStatus> () => {
            return {
                driver,
                steps
            }
        }
    }

    startMobileTour(useDriver: Driver = null,
                     startStepId: string = null,
                     restarted: boolean = false): TourStatus {
        console.log('OETourService mobile tour function');

        const driverOptions = {
            animate: true,
            stageBackground: "transparent",
            allowClose: false,
            padding: 0,
            onReset,
            onNext: () => {
                console.log('global onNext')
            }
        };

        onActivate();

        let driver = useDriver || new Driver(driverOptions);

        let steps;

        console.log(`Orientation ${isPortrait() ? 'portrait' : 'landscape'}, ${window.orientation} ${window.innerWidth} ${window.innerHeight}`);

        const workAroundMoveNext = (id) => _workAroundMoveNext(driver, steps, id);

        const simpleNext = (aStep) => {
            driver.preventMove();
            requestAnimationFrame(() => workAroundMoveNext(aStep));
        };

        /*const linzBtnName = `Report based on LINZ Parcels`;*/
        const showPane = (paneId: string) => {
            this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, paneId);
        };

        const hidePane = (paneId: string) => {
            this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, paneId);
        };

        const taranakiCoordinate: Coordinate = [1691574, 5650137];

        steps = [
            {
                _id: 'a',
                element: 'main > div',
                popover: {
                    ...OETourSteps.introTouch,
                    position: 'mid-center'
                },
                onNext: () => {
                    driver.preventMove();
                    resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);
                    waitFor("div.layer-selector-body:not(.ng-hide) .panel[data-title='About Land Capability'] [data-action=expandTopic]")
                        .then(btn => {
                            if (btn.querySelector('.icon-plus')) {
                                btn.click();
                            }

                            return waitFor('.panel-collapse.collapse.in:not(.ng-hide)');
                        })
                        .then(panel => {
                            const items = panel.querySelectorAll('.flex-grid-item input');
                            let currentAction = Promise.resolve();

                            items.forEach((item: HTMLInputElement) => {
                                currentAction = currentAction
                                    .then(() => {
                                        if (!item.checked) {
                                            (<HTMLElement> item).click();
                                            return delay(250);
                                        }
                                        return Promise.resolve();
                                    });
                            });

                            return currentAction;
                        })
                        .then(() => waitFor("div.layer-selector-body:not(.ng-hide) .panel-group .panel[data-title='About Habitats'] .panel-heading input"))
                        .then((checkbox: HTMLInputElement) => {
                            if (!checkbox.checked) checkbox.click()
                        })
                        .then(() => {
                            this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false);
                            this.$rootScope.$broadcast('layerSelector:updateIncludedLayers');
                            this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.LAYERS);
                            return workAroundMoveNext('b');
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'b',
                element: '.pane-container:not(.pane-collapse-xs) .inset-pane #PaneLayers',
                popover: {
                    ...OETourSteps.myLayers,
                    position: isPortrait() ? 'top-center' : 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);
                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);
                    workAroundMoveNext('a')
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor(".inset-pane [data-action=\"showLayerSelector\"]")
                        .then(btn => btn.scrollIntoView())
                        .then(() => workAroundMoveNext('c'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'c',
                element: ".inset-pane [data-action=\"showLayerSelector\"]",
                stageBackground: "#fff",
                popover: {
                    ...OETourSteps.changeLayers,
                    position: isPortrait() ? 'top-center' : 'bottom-right'
                },
                onPrevious: () => simpleNext('b'),
                onNext: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.LAYERS);
                    workAroundMoveNext('d')
                }
            },
            {
                _id: 'd',
                element: 'main',
                popover: {
                    ...OETourSteps.mapPage,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.LAYERS);
                    waitFor('.pane-container:not(.pane-collapse-xs) .inset-pane [data-action=\"showLayerSelector\"]')
                        .then(btn => btn.scrollIntoView())
                        .then(() => workAroundMoveNext('c'))
                        .catch(timeoutHandler(driver));
                    // simpleNext('c');
                },
                onNext: () => simpleNext('e')
            },
            {
                _id: 'e',
                element: '.tile-nav-xs',
                popover: {
                    ...OETourSteps.mainButtons,
                    position: isPortrait() ? 'bottom-center' : 'right-center'
                },
                onPrevious: () => simpleNext('d'),
                onNext: () => simpleNext('f')
            },
            {
                _id: 'f',
                element: '.tile-nav-xs li.search',
                popover: {
                    ...OETourSteps.searchA,
                    position: isPortrait() ? 'bottom-center' : 'right-center'
                },
                onPrevious: () => simpleNext('e'),
                onNext: () => {
                    driver.preventMove();
                    waitFor('.tile-nav-xs button[title=Search]')
                        .then(searchBtn => {
                            searchBtn.click();
                            return delay(100);
                        })
                        .then(() => waitFor('.modal.in:not(.ng-enter)'))
                        .then(() => waitFor('.modal-content input[type=search]'))
                        .then((search: HTMLInputElement) => {
                            search.blur();
                            return workAroundMoveNext('f2');
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'f2',
                element: '.modal-dialog',
                popover: {
                    ...OETourSteps.searchB,
                    position: isPortrait() ? 'bottom-center' : 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    waitFor('[data-dismiss="modal"]')
                        .then((dismiss) => {
                            dismiss.click();
                            return workAroundMoveNext('e');
                        })
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('.modal-dialog input')
                        .then((input: HTMLInputElement) => typeIntoInput(input, "Mount Taranaki"))
                        .then(() => {
                            const coordinate = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCoordinate, window['map'], 8);
                            this.searchService.animateZoomTo(coordinate, 8);
                            this.searchService.highlightLocation(coordinate, 4000);
                            (<HTMLElement> document.querySelector('[data-dismiss="modal"]'))
                                .click();
                        })
                        .then(() => delay(500))
                        .then(() => workAroundMoveNext('g'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'g',
                element: '.ol-zoom',
                popover: {
                    ...OETourSteps.zoomTouch,
                    position: isPortrait() ? 'right-center' : 'right-top'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(L.Events.OL_RESET_EXTENT, {
                        detail: {
                            padding: this.coordinateAdjustmentService.padding(),
                            duration: 500
                        }
                    });

                    simpleNext('f')
                },
                onNext: () => {
                    showPane(Panes.Ids.LAYERS);
                    simpleNext('h');
                }
            },
            {
                _id: 'h',
                element: '.inset-pane #PaneLayers',
                popover: {
                    ...OETourSteps.mapLayersMobile,
                    position: isPortrait() ? 'top-center' : 'left-center'
                },
                onPrevious: () => {
                    hidePane(Panes.Ids.LAYERS);
                    simpleNext('g')
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('.inset-pane #PaneLayers label[title="Land Capability"] ~ [data-action="layerSelector:expandGroup"]')
                        .then(btn => btn.click())
                        .then(() => waitFor('.inset-pane #PaneLayers label[title="Most intensive, sustainable land use possible"]'))
                        .then((label) => {
                            label.click();
                            return delay(100);
                        })
                        .then(() => hidePane(Panes.Ids.LAYERS))
                        .then(() => waitFor('.pane-collapse-xs .inset-pane'))
                        .then(() => workAroundMoveNext('i'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'i',
                element: '.oe-map-container .features-menu',
                popover: {
                    ...OETourSteps.reportControlsMobile,
                    position: 'top-right'
                },
                onPrevious: () => {
                    driver.preventMove();
                    showPane(Panes.Ids.LAYERS);
                    waitFor('.inset-pane #PaneLayers label[title="Suitability for Arable Cropping"]')
                        .then(() => workAroundMoveNext('h'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('.oe-map-container .features-menu [data-action="toggleFeaturesMenu"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('j'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'j',
                stageBackground: "#fff",
                element: '.oe-map-container .features-menu [data-action="toggleDropPinMode"]',
                popover: {
                    ...OETourSteps.dropPinAMobile,
                    position: isPortrait() ? 'bottom-right' : 'left-top'
                },
                onNext: () => {
                    driver.preventMove();

                    waitFor('.features-menu [data-action="toggleDropPinMode"]')
                        .then(btn => {
                            btn.click();
                            workAroundMoveNext('k');
                        })
                        .catch(timeoutHandler(driver));
                    // simpleNext('k');
                },
                onPrevious: () => {
                    driver.preventMove();
                    waitFor('.features-menu [data-action="toggleFeaturesMenu"]')
                        .then(btn => {
                            btn.click();
                            simpleNext('i')
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'k',
                element: 'main',
                popover: {
                    ...OETourSteps.dropPinBTouch,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    waitFor('.features-menu [data-action="toggleFeaturesMenu"]')
                        .then(btn => {
                            btn.click();
                            btn.click();
                            workAroundMoveNext('j');
                        })
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    // drop a pin
                    driver.preventMove();

                    const coord = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCoordinate, window['map'], 8);
                    this.$rootScope.$broadcast(GeoAppEvents.queryPoint, coord);
                    waitFor('.inset-pane-xs .report-preview')
                        .then(() => workAroundMoveNext('l'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'l',
                element: '.inset-pane-xs',
                popover: {
                    ...OETourSteps.pinSummaryTouch,
                    position: isPortrait() ? 'top-center' : 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    this.$rootScope.$broadcast(GeoAppEvents.removeUnsavedPins);
                    hidePane(Panes.Ids.FEATURES);

                    waitFor('.pane-container.pane-collapse-xs')
                        .then(() => waitFor('.features-menu [data-action="toggleFeaturesMenu"]'))
                        .then(btn => btn.click())
                        .then(() => waitFor('[data-action="toggleDropPinMode"]'))
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('k'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    hidePane(Panes.Ids.FEATURES);
                    this.$rootScope.$broadcast(GeoAppEvents.removeUnsavedPins);
                    waitFor('.pane-container.pane-collapse-xs')
                        .then(() => waitFor('.features-menu [data-action="toggleFeaturesMenu"]'))
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('m'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'm',
                element: '.features-menu .fab-menu-list',
                stageBackground: "#fff",
                popover: {
                    ...OETourSteps.areaReports,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    const coord = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCoordinate, window['map'], 8);
                    this.$rootScope.$broadcast(GeoAppEvents.queryPoint, coord);
                    delay(250)
                        .then(() => workAroundMoveNext('l'))
                },
                onNext: () => simpleNext('n')
            },
            {
                _id: 'n',
                element: '.features-menu [data-action=togglePredefinedFeatureMode][title*=LINZ]',
                stageBackground: "#fff",
                popover: {
                    ...linzDemo,
                    position: isPortrait() ? 'top-right' : 'left-center'
                },
                onPrevious: () => simpleNext('m'),
                onNext: () => {
                    driver.preventMove();
                    waitFor('.features-menu [data-action=togglePredefinedFeatureMode][title*=LINZ]')
                        .then(btn => btn.click())
                        .then(() => waitFor(".features-menu .active-mode-title"))
                        .then(() => workAroundMoveNext('n2'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'n2',
                element: 'main',
                popover: {
                    ...linzDemoAction,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    waitFor('.features-menu [data-action="toggleFeaturesMenu"]')
                        .then(btn => {
                            btn.click();
                            btn.click();
                            simpleNext('n');
                        })
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();

                    this.mapService.getActiveClickInteraction().then((interaction) => {
                            simulateOpenLayersEvent(this.mapService.map, interaction, 'click', 0, 0);
                            this.fitTaranaki();
                            return delay(50);
                        })
                        .then(() => workAroundMoveNext('o'))


                }
            },
            {
                _id: 'o',
                element: '.inset-pane-xs #PaneFeatures',
                popover: {
                    ...OETourSteps.linzSummary,
                    position: isPortrait() ? 'top-center' : 'left-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    hidePane(Panes.Ids.FEATURES);
                    waitFor('.pane-container.pane-collapse-xs')
                        .then(() => waitFor('.features-menu [data-action="toggleFeaturesMenu"]'))
                        .then(btn => btn.click())
                        .then(() => waitFor('.features-menu [data-action=togglePredefinedFeatureMode][title*=LINZ]'))
                        .then(btn => {
                            btn.click();
                            workAroundMoveNext('n2');
                        })
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('.inset-pane .report-preview [data-action="createFullReport"]')
                        .then(btn => {
                            btn.scrollIntoView();
                            workAroundMoveNext('p');
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'p',
                element: '.inset-pane .report-preview [data-action="createFullReport"]',
                stageBackground: '#fff',
                popover: {
                    ...OETourSteps.linzFullReport,
                    position: isPortrait() ? 'top-center' : 'left-center'
                },
                onPrevious: () => {
                    simpleNext('o')
                },
                onNext: () => {
                    // display a complete report... somehow
                    driver.preventMove();
                    this.showExampleAreaReport()
                        .then(() => workAroundMoveNext('q'));
                }
            },
            {
                _id: 'q',
                element: '.fullscreen-report',
                popover: {
                    ...OETourSteps.fullReports,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    waitFor('.fullscreen-report i.close-button')
                        .then(btn => btn.click())
                        .then(() => waitFor('.inset-pane .report-preview [data-action="createFullReport"]'))
                        .then(btn => btn.scrollIntoView())
                        .then(() => workAroundMoveNext('p'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    // display a complete report... somehow
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.FEATURES);

                    driver.preventMove();
                    waitFor('.fullscreen-report i.close-button')
                        .then(btn => btn.click())
                        .then(() => delay(250))
                        .then(() => {
                            this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);
                            workAroundMoveNext('t')
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 't',
                element: 'main',
                popover: {
                    ...OETourSteps.conclusion,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false);
                    this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.FEATURES);
                    this.fitTaranaki();
                    driver.preventMove();
                    waitFor('.inset-pane [data-action=togglePredefinedFeatureMode][title*=LINZ]')
                        .then(btn => btn.click())
                        .then(() => this.mapService.getActiveClickInteraction())
                        .then((interaction) => simulateOpenLayersEvent(this.mapService.map, interaction, 'click', 0, 0))
                        .then(() => this.showExampleAreaReport())
                        .then(() => workAroundMoveNext('q'))
                        .catch(timeoutHandler(driver));
                }
            }
        ];

        console.log(`Tour: orientation ${isPortrait() ? 'portrait' : 'landscape'}, ${window.innerWidth} x ${window.innerHeight}`);

        const onNextAfterRest = () => {
            driver.preventMove();
            this.onReset().then(() => {
                steps.shift();
                waitFor(steps[0].element)
                    .then(() => {
                        this.startMobileTour(driver, 'a', false);
                    })
                    .catch(timeoutHandler(driver));
            });
        };

        if (restarted && isPortrait()) {
            console.log('Showing "restarted" message');
            driver.reset();
            steps.unshift({
                _id: 'restarted',
                element: '#ng-body',
                popover: {
                    ...OETourSteps.tourRestarted,
                    position: 'mid-center'
                },
                onNext: onNextAfterRest
            })
        } else if (!isPortrait()) {
            console.log('Showing "please rotate" message');
            driver.reset();
            steps.unshift({
                _id: 'a0',
                element: 'body #ng-body',
                popover: {
                    title: "Please rotate your device",
                    description: `The tour will work best in <b>portrait.</b>`,
                    position: 'mid-center'
                },
                onNext: () => {
                    driver.preventMove();
                    this.onReset().then(() => workAroundMoveNext('a'))
                }
            });
        }

        driver.defineSteps(steps);

        // startWhenReady(driver, appScope, appElement, true);

        driver.start(0);

        return <TourStatus> () => {
            return {
                driver,
                steps
            }
        }
    }

    startTabletTour(useDriver: Driver = null,
                    startStepId: string = null,
                    restarted: boolean = false): TourStatus {
        const driverOptions = {
            animate: true,
            stageBackground: "transparent",
            allowClose: false,
            onReset,
            onNext: () => {
                console.log('global onNext')
            }
        };

        onActivate();

        let driver = useDriver || new Driver(driverOptions);

        let steps;

        const workAroundMoveNext = (id) => _workAroundMoveNext(driver, steps, id);

        const simpleNext = (aStep) => {
            driver.preventMove();
            requestAnimationFrame(() => workAroundMoveNext(aStep));
        };

        const showPane = (paneId: string) => this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, paneId);
        const hidePane = (paneId: string) =>  this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, paneId);

        const taranakiCoordinate: Coordinate = [1691574, 5650137];

        steps = [
            {
                _id: 'a',
                element: '.pane-container:not(.pane-collapse-layer-selector) .meta-layer-selector-container',
                popover: {
                    ...OETourSteps.introTouch,
                    position: 'mid-center'
                },
                onNext: () => {
                    driver.preventMove();
                    resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);

                    waitFor('[data-action="layerSelectorTab:topic"]')
                        .then(tab => tab.click())
                        .then(() => waitFor("div.layer-selector-body:not(.ng-hide) .panel[data-title='About Land Capability'] [data-action=expandTopic]"))
                        .then(btn => {
                            if (btn.querySelector('.icon-plus')) {
                                btn.click();
                            }

                            return waitFor('.panel-collapse.collapse.in:not(.ng-hide)');
                        })
                        .then(panel => {
                            const items = panel.querySelectorAll('.flex-grid-item input');
                            let currentAction = Promise.resolve();

                            items.forEach((item: HTMLInputElement) => {
                                currentAction = currentAction
                                    .then(() => {
                                        if (!item.checked) {
                                            (<HTMLElement> item).click();
                                            return delay(250);
                                        }
                                        return Promise.resolve();
                                    });
                            });

                            return currentAction;
                        })
                        .then(() => waitFor("div.layer-selector-body:not(.ng-hide) .panel-group .panel[data-title='About Habitats'] .panel-heading input"))
                        .then((checkbox: HTMLInputElement) => {
                            if (!checkbox.checked) checkbox.click()
                        })
                        .then(() => {
                            this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false);
                            this.$rootScope.$broadcast('layerSelector:updateIncludedLayers');
                            showPane(Panes.Ids.LAYERS);
                            return workAroundMoveNext('b');
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'b',
                element: '.inset-pane #PaneLayers',
                popover: {
                    ...OETourSteps.myLayers,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    resetMap(this.$rootScope, this.mapService, this.layerTreeManipulationService);
                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);
                    workAroundMoveNext('a');
                },
                onNext: () => {
                    console.log(`isPortrait? ${isPortrait()}`);
                    simpleNext('c')
                }
            },
            {
                _id: 'c',
                element: ".inset-pane [data-action=showLayerSelector]",
                stageBackground: "#fff",
                popover: {
                    ...OETourSteps.changeLayers,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => simpleNext('b'),
                onNext: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.LAYERS);
                    workAroundMoveNext('d')
                }
            },
            {
                _id: 'd',
                element: 'main',
                popover: {
                    ...OETourSteps.mapPage,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.LAYERS);
                    simpleNext('c');
                },
                onNext: () => simpleNext('e')
            },
            {
                _id: 'e',
                element: '.tile-nav-sm',
                popover: {
                    ...OETourSteps.mainButtons,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => simpleNext('d'),
                onNext: () => simpleNext('f')
            },
            {
                _id: 'f',
                element: '.tile-nav-sm li.search',
                popover: {
                    ...OETourSteps.searchA,
                    position: isPortrait() ? undefined : 'right-center'
                },
                onPrevious: () => simpleNext('e'),
                onNext: () => {
                    driver.preventMove();
                    waitFor('.tile-nav-sm button[title=Search]')
                        .then(searchBtn => {
                            searchBtn.click();
                            return delay(100);
                        })
                        .then(() => waitFor('.modal.in:not(.ng-enter)'))
                        .then(() => waitFor('.modal-content input[type=search]'))
                        .then((search: HTMLInputElement) => {
                            search.blur();
                            return workAroundMoveNext('f2');
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'f2',
                element: '.modal-dialog',
                popover: {
                    ...OETourSteps.searchB,
                    position: 'bottom-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    waitFor('[data-dismiss="modal"]')
                        .then((btn) => {
                            btn.click();
                            workAroundMoveNext('f')
                        })
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('.modal-dialog input')
                        .then((input: HTMLInputElement) => typeIntoInput(input, "Mount Taranaki"))
                        .then(() => {
                            const coordinate = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCoordinate, window['map'], 8);
                            this.searchService.animateZoomTo(coordinate, 8);
                            this.searchService.highlightLocation(coordinate, 4000);
                            (<HTMLElement> document.querySelector('[data-dismiss="modal"]'))
                                .click();
                        })
                        .then(() => delay(500))
                        .then(() => workAroundMoveNext('g'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'g',
                element: '.ol-zoom',
                popover: {
                    ...OETourSteps.zoomTouch,
                    position: 'right-center'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(L.Events.OL_RESET_EXTENT, {
                        detail: {
                            padding: this.coordinateAdjustmentService.padding(),
                            duration: 500
                        }
                    });

                    simpleNext('f')
                },
                onNext: () => {
                    showPane(Panes.Ids.LAYERS);
                    simpleNext('h');
                }
            },
            {
                _id: 'h',
                element: '.inset-pane #PaneLayers',
                popover: {
                    ...OETourSteps.mapLayersTablet,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => {
                    hidePane(Panes.Ids.LAYERS);
                    simpleNext('g')
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('#PaneLayers label[title="Most intensive, sustainable land use possible"]')
                        .then((label) => {
                            label.click();
                            return delay(100);
                        })
                        .then(() => {
                            hidePane(Panes.Ids.LAYERS);
                        })
                        .then(() => delay(250))
                        .then(() => {
                            showPane(Panes.Ids.FEATURES);
                            return waitFor('.inset-pane .features-container')
                        })
                        .then(() => workAroundMoveNext('i'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'i',
                element: '.inset-pane',
                popover: {
                    ...OETourSteps.reportPane,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    showPane(Panes.Ids.LAYERS);
                    waitFor('.inset-pane #PaneLayers label[title="Suitability for Arable Cropping"]')
                        .then(() => workAroundMoveNext('h'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'j',
                stageBackground: "#fff",
                element: '.inset-pane .add-feature-action',
                popover: {
                    ...OETourSteps.dropPinAMobile,
                    position: isPortrait() ? undefined : 'right-center'
                },
                onNext: () => {
                    driver.preventMove();

                    waitFor('.inset-pane [data-action="toggleDropPinMode"]')
                        .then(btn => {
                            btn.click();
                            return workAroundMoveNext('k');
                        })
                        .catch(timeoutHandler(driver));
                },
                onPrevious: () => simpleNext('i')
            },
            {
                _id: 'k',
                element: 'main',
                popover: {
                    ...OETourSteps.dropPinB,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    waitFor('.inset-pane [data-action="toggleDropPinMode"]')
                        .then(btn => {
                            btn.click();
                            return workAroundMoveNext('j');
                        })
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    // drop a pin
                    driver.preventMove();

                    const coord = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCoordinate, window['map'], 8);
                    this.$rootScope.$broadcast(GeoAppEvents.queryPoint, coord);
                    waitFor('.inset-pane-sm .report-preview')
                        .then(() => workAroundMoveNext('l'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'l',
                element: '.inset-pane-sm',
                popover: {
                    ...OETourSteps.pinSummaryTouch,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    waitFor('.inset-pane .add-feature-action')
                        .then(() => waitFor('[data-action="toggleDropPinMode"]'))
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('k'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    waitFor('#PaneFeatures .add-feature-action')
                        .then(() => workAroundMoveNext('m'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'm',
                element: '.inset-pane .features-container',
                stageBackground: "#fff",
                popover: {
                    ...OETourSteps.areaReports,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    const coord = this.coordinateAdjustmentService.adjustCentreForScreenSpace(taranakiCoordinate, window['map'], 8);
                    this.$rootScope.$broadcast(GeoAppEvents.queryPoint, coord);
                    delay(250)
                        .then(() => workAroundMoveNext('l'))
                },
                onNext: () => simpleNext('n')
            },
            {
                _id: 'n',
                element: '.inset-pane [data-action=togglePredefinedFeatureMode][title*=LINZ]',
                stageBackground: "#fff",
                popover: {
                    ...linzDemo,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => simpleNext('m'),
                onNext: () => {
                    driver.preventMove();
                    waitFor('.inset-pane [data-action=togglePredefinedFeatureMode][title*=LINZ]')
                        .then(btn => btn.click())
                        .then(() => this.mapService.getActiveClickInteraction())
                        .then((interaction) => simulateOpenLayersEvent(this.mapService.map, interaction, 'click', 0, 0))
                        .then(() => {
                            this.fitTaranaki();
                            workAroundMoveNext('o')
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'o',
                element: '.inset-pane-sm',
                popover: {
                    ...OETourSteps.linzSummary,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    workAroundMoveNext('n');
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('.inset-pane .report-preview [data-action="createFullReport"]')
                        .then(btn => {
                            btn.scrollIntoView();
                            workAroundMoveNext('p');
                        })
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'p',
                element: '.inset-pane .report-preview [data-action="createFullReport"]',
                stageBackground: '#fff',
                popover: {
                    ...OETourSteps.linzFullReport,
                    position: isPortrait() ? 'top-center' : 'right-center'
                },
                onPrevious: () => {
                    simpleNext('o')
                },
                onNext: () => {
                    // display a complete report... somehow
                    driver.preventMove();
                    this.showExampleAreaReport()
                        .then(() => workAroundMoveNext('q'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'q',
                element: '.fullscreen-report',
                popover: {
                    ...OETourSteps.fullReports,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();

                    waitFor('.fullscreen-report i.close-button')
                        .then(btn => btn.click())
                        .then(() => waitFor('.inset-pane .report-preview [data-action="createFullReport"]'))
                        .then(btn => btn.scrollIntoView())
                        .then(() => workAroundMoveNext('p'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    // display a complete report... somehow
                    this.$rootScope.$broadcast(FeaturesEvents.hideReport);
                    this.$rootScope.$broadcast(Panes.Events.HIDE_PANE, Panes.Ids.FEATURES);
                    driver.preventMove();
                    waitFor('.fullscreen-report i.close-button')
                        .then(btn => btn.click())
                        .then(() => delay(250))
                        .then(() => workAroundMoveNext('r'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 'r',
                element: '[data-action="showPrintWindow"]',
                stageBackground: '#fff',
                popover: {
                    ...OETourSteps.printTool,
                    position: 'bottom-center'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.FEATURES);
                    driver.preventMove();
                    this.fitTaranaki();
                    waitFor('.inset-pane [data-action=togglePredefinedFeatureMode][title*=LINZ]')
                        .then(btn => btn.click())
                        .then(() => this.mapService.getActiveClickInteraction())
                        .then((interaction) => simulateOpenLayersEvent(this.mapService.map, interaction, 'click', 0, 0))
                        .then(() => this.showExampleAreaReport())
                        .then(() => workAroundMoveNext('q'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    driver.preventMove();
                    waitFor('[data-action="showPrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('s'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 's',
                element: '.print-pane',
                popover: {
                    ...OETourSteps.printWindow,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    driver.preventMove();
                    waitFor('[data-action="closePrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('r'))
                        .catch(timeoutHandler(driver));
                },
                onNext: () => {
                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, true);

                    waitFor('[data-action="closePrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('t'))
                        .catch(timeoutHandler(driver));
                }
            },
            {
                _id: 't',
                element: 'main',
                popover: {
                    ...OETourSteps.conclusion,
                    position: 'mid-center'
                },
                onPrevious: () => {
                    this.$rootScope.$broadcast(GeoAppEvents.toggleLayerSelector, false);

                    driver.preventMove();
                    waitFor('[data-action="showPrintWindow"]')
                        .then(btn => btn.click())
                        .then(() => workAroundMoveNext('s'))
                        .catch(timeoutHandler(driver));
                }
            }
        ];

        if (restarted) {
            steps.unshift({
                _id: 'restarted',
                element: '#ng-body',
                popover: {
                    ...OETourSteps.tourRestarted,
                    position: 'mid-center'
                },
                onNext: () => {
                    driver.preventMove();
                    this.onReset().then(() => {
                        // steps.shift();
                        // driver.reset(true);
                        // waitFor(steps[0].element).then(() => {
                        //     this.startTabletTour(driver, 'a', false);
                        // });
                        workAroundMoveNext('a')
                    });
                }
            })
        }

        driver.defineSteps(steps);

        // startWhenReady(driver, appScope, appElement, true);

        driver.start(0);

        return <TourStatus> () => {
            return {
                driver,
                steps/*,
                onOrientationChange: () => setTimeout(() => {
                    console.log('redefining steps');
                    const newSteps = steps(true);
                    driver.reset(true);
                    setTimeout(() => {
                        this.onReset();
                        driver.defineSteps(newSteps);
                        driver.start(0);
                    }, 1);
                }, 100)*/
            }
        }
    }
}